import React from 'react';
import { Box, Typography, Chip, Alert } from '@mui/material';
import { Group as GroupIcon, TrendingUp as TrendingUpIcon, TrendingDown as TrendingDownIcon, Notifications as NotificationsIcon } from '@mui/icons-material';
import ShareDialog from './ShareDialog';
import { shareAlertReport, getAlertReportShares } from '../../services/alert-report-sharing.service';

import { useSelector } from 'react-redux';

const ShareAlertReportDialog = ({ 
    alertReport, 
    onClose, 
    onShareSuccess 
}) => {
    const activeFacilities = useSelector((state) => state.activeFacilities.facilities);
    
    const handleShare = async (alertReportId, selectedUsers, message, isUniversal) => {
        // Include custom alert information if this is a custom alert share
        const shareData = {
            alertReportId,
            recipientIds: selectedUsers,
            shareMessage: message,
            isUniversal,
            activeFacilities
        };

        if (alertReport?.shareType === "CUSTOM_ALERT_ONLY") {
            shareData.shareType = "CUSTOM_ALERT_ONLY";
            shareData.customAlertSlug = alertReport.customAlertSlug;
        }

        return await shareAlertReport(shareData.alertReportId, shareData.recipientIds, shareData.shareMessage, shareData.isUniversal, shareData.customAlertSlug, shareData.shareType, shareData.activeFacilities);
    };

    const handleLoadShares = async (alertReportId) => {
        // Pass custom alert information if this is a custom alert share
        if (alertReport?.shareType === "CUSTOM_ALERT_ONLY") {
            return await getAlertReportShares(alertReportId, alertReport.customAlertSlug, alertReport.shareType);
        }
        return await getAlertReportShares(alertReportId);
    };

    const renderItemInfo = (item, existingShares) => {
        const isCustomAlert = item?.shareType === "CUSTOM_ALERT_ONLY";
        const itemName = isCustomAlert ? item?.name : `${item?.page || 'Alert'} settings`;
        
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <GroupIcon sx={{ color: '#2196f3' }} />
                    <Typography variant="body1" sx={{ color: '#333333', fontSize: '0.9rem' }}>
                        Share "{itemName}" with team members
                    </Typography>
                </Box>

                                <Alert severity={isCustomAlert ? "warning" : "info"} sx={{ mb: 1 }}>
                    {isCustomAlert ? (
                        <>
                            Share this custom alert with other users. Recipients will receive a notification and can choose to accept or decline.
                            <Box sx={{ mt: 1 }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Important Notes:
                                </Typography>
                                <Box component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
                                    <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                        You are sharing only this specific custom alert, not the entire alert report.
                                    </Typography>
                                    <Typography component="li" variant="body2">
                                        Shared alerts are only enabled in the facilities selected at the time of sharing; they're disabled elsewhere.
                                    </Typography>
                                </Box>
                            </Box>
                        </> 
                    ) : (
                        <>
                            Share your alert settings with other users. Recipients will receive a notification and can choose to accept or decline.
                        </>
                    )}
                    {existingShares.length > 0 && (
                        <Box mt={1}>
                            <strong>Current shares:</strong> {existingShares.length} user(s) already have access to {isCustomAlert ? 'this custom alert' : 'these alert settings'}.
                        </Box>
                    )}
                </Alert>

                <Box sx={{ p: 2, background: '#f8f9fa', borderRadius: 1, border: '1px solid rgba(0,0,0,0.1)' }}>
                    {isCustomAlert ? (
                        <>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                <strong>Custom Alert:</strong> {item?.name || 'N/A'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                <strong>Page:</strong> {item?.page || 'N/A'}
                            </Typography>
                            {item?.customAlert?.[0] && (
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    <strong>Alert Type:</strong> {item.customAlert[0].alertType || 'Custom'}
                                </Typography>
                            )}
                        </>
                    ) : (
                        <>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                <strong>Page:</strong> {item?.page || 'N/A'}
                            </Typography>

                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                <strong>Alert Type:</strong> {item?.type?.join(', ') || 'N/A'}
                            </Typography>
                        </>
                    )}

                    <Box display="flex" gap={1} mt={1} flexWrap="wrap">
                        {item?.isRising && (
                            <Chip 
                                label="Trending Up" 
                                size="small" 
                                icon={<TrendingUpIcon sx={{ fontSize: '16px !important' }} />}
                                sx={{
                                    background: 'rgba(76, 175, 80, 0.1)',
                                    color: '#4caf50',
                                    border: '1px solid rgba(76, 175, 80, 0.3)',
                                    height: '24px',
                                    fontSize: '0.75rem'
                                }}
                            />
                        )}
                        {item?.isDropping && (
                            <Chip 
                                label="Trending Down" 
                                size="small" 
                                icon={<TrendingDownIcon sx={{ fontSize: '16px !important' }} />}
                                sx={{
                                    background: 'rgba(255, 152, 0, 0.1)',
                                    color: '#ff9800',
                                    border: '1px solid rgba(255, 152, 0, 0.3)',
                                    height: '24px',
                                    fontSize: '0.75rem'
                                }}
                            />
                        )}
                        {item?.isActive !== undefined && (
                            <Chip 
                                label={item.isActive ? 'Active' : 'Inactive'} 
                                size="small" 
                                icon={<NotificationsIcon sx={{ fontSize: '16px !important' }} />}
                                sx={{
                                    background: item.isActive ? 'rgba(33, 150, 243, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                                    color: item.isActive ? '#2196f3' : '#f44336',
                                    border: `1px solid ${item.isActive ? 'rgba(33, 150, 243, 0.3)' : 'rgba(244, 67, 54, 0.3)'}`,
                                    height: '24px',
                                    fontSize: '0.75rem'
                                }}
                            />
                        )}
                    </Box>
                </Box>
            </Box>
        );
    };

    const isCustomAlert = alertReport?.shareType === "CUSTOM_ALERT_ONLY";
    const dialogTitle = isCustomAlert ? "Share Custom Alert" : "Share Alert Settings";
    const shareButtonText = isCustomAlert ? "Share Custom Alert" : "Share Alert";

    return (
        <ShareDialog
            open={true}
            onClose={onClose}
            title={dialogTitle}
            item={alertReport}
            itemType="ALERT"
            onShare={handleShare}
            onLoadShares={handleLoadShares}
            renderItemInfo={renderItemInfo}
            shareButtonText={shareButtonText}
            allowUniversal={!isCustomAlert} // Custom alerts don't support universal access
        />
    );
};

export default ShareAlertReportDialog; 